import { updatePassword } from "aws-amplify/auth";
import { Field, Form, Formik } from "formik";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Redirect, withRouter } from "react-router-dom";
import * as Yup from "yup";
import { client } from "../../../App";
import UserContext from "../../../contexts/UserContext";
import { getValidationRule } from "../../../helpers/helpers";
import { NOTIFY_UPDATE_PASSWORD, getEvictionForMutation } from "../../../helpers/mutations";
import PasswordModalInfoMatch from "../../common/PasswordModalInfoMatch/PasswordModalInfoMatch";
import "./CustomChangePassword.scss";

class CustomChangePassword extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      step: "form",
      errMsg: {},
      fieldInfo: false,
    };
  }

  closeModal = () => {
    this.setState({ fieldInfo: false });
  };

  handleChangePassword = async (
    values,
    { resetForm, setErrors, setStatus },
    location
  ) => {
    const { oldPassword, newPassword } = values;

    try {
      await updatePassword({
        oldPassword: oldPassword,
        newPassword: newPassword,
      });

      await client().mutate({
        mutation: NOTIFY_UPDATE_PASSWORD,
        update: (cache: any) => {
          const fieldsToEvict = getEvictionForMutation("notifyUpdatePassword");
          fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
          cache.gc();
        },
      });

      this.setState({ step: "done" });
    } catch (err) {
      console.log("ERROR Change Password: ", err);

      const errorCode = err.name || err.code;

      switch (errorCode) {
        case "NotAuthorizedException":
        case "InvalidParameterException":
        case "LimitExceededException":
          setErrors({
            oldPassword: "La password inserita non è corretta. ",
          });
          break;
        default:
          setErrors({ oldPassword: "Problema di connessione " });
          break;
      }
    }
  };

  render() {
    const { step } = this.state;
    const { t } = this.props;
    const location = this.props.location;
    console.log(location);

    if (step === "done") return <Redirect to={"/area-riservata"} />;

    const validPasswordMinNumber = getValidationRule(
      "passwordMinimumNumberOfChar"
    );
    const validPaswordUppercase = getValidationRule("passwordUppercaseChar");
    const validPaswordLowercase = getValidationRule("passwordLowercaseChar");
    const validPaswordNumeric = getValidationRule("passwordNumericChar");
    const validPaswordSpecial = getValidationRule("passwordSpecialChar");

    let schemaValidation = Yup.object().shape({
      oldPassword: Yup.string().required(
        t("error.changePassword.oldPassword.required")
      ),
      newPassword: Yup.string()
        .optional()
        .notOneOf([Yup.ref("oldPassword")], t("cases.password.old.error"))
        .matches(validPasswordMinNumber, {
          message: t("errors.change.password.validation.match"),
        })
        .matches(validPaswordUppercase, {
          message: t("errors.change.password.validation.match"),
        })
        .matches(validPaswordLowercase, {
          message: t("errors.change.password.validation.match"),
        })
        .matches(validPasswordMinNumber, {
          message: t("errors.change.password.validation.match"),
        })
        .matches(validPaswordNumeric, {
          message: t("errors.change.password.validation.match"),
        })
        .matches(validPaswordSpecial, {
          message: t("errors.change.password.validation.match"),
        })
        .required(t("error.changePassword.newPassword.required")),
      newPasswordAgain: Yup.string()
        .required(t("error.changePassword.newPassword.repeat.required"))
        .oneOf(
          [Yup.ref("newPassword"), null],
          t("error.registration.confirmPassword.combine")
        ),
    });

    return (
      <div
        className={"CustomChangePassword mt-5"}
        data-testid="CustomChangePassword"
      >
        {step === "form" ? (
          <Formik
            initialValues={{
              oldPassword: "",
              newPassword: "",
              newPasswordAgain: "",
            }}
            validationSchema={schemaValidation}
            onSubmit={(value, props) =>
              this.handleChangePassword(
                value,
                props,
                location?.state?.passwordExpiration
              )
            }
          >
            {({ errors, touched }) => (
              <Form noValidate autoComplete="off" role="presentation">
                <div className="row">
                  <div className="col-xs-12 col-md-10 offset-md-1">
                    <p
                      className={
                        "case-step-mandatory-fields-legend paragraph-small-fs9"
                      }
                    >
                      {t("mandatory.fields.legend")}
                    </p>
                    <div className={"row"}>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label
                            className="paragraph-small-fs9"
                            htmlFor="oldPassword"
                          >
                            {t("change.password.oldPassword")} *
                          </label>
                          <Field
                            name={"oldPassword"}
                            id="oldPassword"
                            type="password"
                            aria-describedby={
                              errors.oldPassword && touched.oldPassword
                                ? "oldPassword-error"
                                : null
                            }
                            required={true}
                            className={
                              errors.oldPassword && touched.oldPassword
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder={t(
                              "change.password.oldPassword.placeholder"
                            )}
                          />
                          {errors.oldPassword && touched.oldPassword ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"oldPassword-error"}
                            >
                              {errors.oldPassword}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className={"row"}>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label
                            className="paragraph-small-fs9"
                            htmlFor="newPassword"
                          >
                            {t("change.password.newPassword")}*
                            <button
                              type="button"
                              className={"form-info-button"}
                              aria-label={t("aria.form.info.button")}
                              aria-haspopup="dialog"
                              aria-controls={"newPassword-info-modal"}
                              onClick={() => {
                                this.setState({
                                  fieldInfo: true,
                                });
                              }}
                            >
                              <span
                                className="form-icon-info"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </label>
                          <Field
                            name={"newPassword"}
                            id="newPassword"
                            type="password"
                            aria-describedby={
                              errors.newPassword && touched.newPassword
                                ? "newPassword-error"
                                : null
                            }
                            required={true}
                            className={
                              errors.newPassword && touched.newPassword
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder={t(
                              "change.password.newPassword.placeholder"
                            )}
                          />
                          {errors.newPassword && touched.newPassword ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"newPassword-error"}
                            >
                              {errors.newPassword}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label
                            className="paragraph-small-fs9"
                            htmlFor="newPasswordAgain"
                          >
                            {t("change.password.confirmPassword")} *
                          </label>
                          <Field
                            name={"newPasswordAgain"}
                            id="newPasswordAgain"
                            type="password"
                            className={
                              errors.newPasswordAgain &&
                              touched.newPasswordAgain
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            aria-describedby={
                              errors.newPasswordAgain &&
                              touched.newPasswordAgain
                                ? "newPasswordAgain-error"
                                : null
                            }
                            required={true}
                            placeholder={t(
                              "change.password.confirmPassword.placeholder"
                            )}
                          />
                          {errors.newPasswordAgain &&
                          touched.newPasswordAgain ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"newPasswordAgain-error"}
                            >
                              {errors.newPasswordAgain}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className={"row"}>
                      <div className="col-lg-12 col-md-12">
                        <div className={"row footer-form-actions"}>
                          <div className={"col text-center"}>
                            <button type="submit" className={"btn btn-primary"}>
                              {t("common.updateBtn")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {this.state.fieldInfo ? (
                  <PasswordModalInfoMatch
                    show={this.state.fieldInfo}
                    onClose={() => this.setState({ fieldInfo: false })}
                  />
                ) : null}
              </Form>
            )}
          </Formik>
        ) : (
          <div className="row">
            <div className="col-xs-12 col-md-6 offset-md-3">
              <div className={"row"}>
                <div className="col-lg-12 col-md-12 text-center">
                  <h3>{t("change.password.update")}</h3>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(withTranslation("translation")(CustomChangePassword));
